/**
 * 会话冷启场景适配（§5.5 / §7）
 *
 * 触发：session bootstrap 完成后会话内只跑一次（与当前 Tab / 路由无关）。
 * 本期仅 ResolveOffersV2 一路。
 * 聚合：race(settled, 2s) → 若有可信 activeScene 则 setMaxPerEntry + resetEntry + onScene
 * 晚到补 tryShow；严格更高优先可一次抢占（dismiss replace，不额外计数）。
 */

import { ActivityApi, type IResolveOffersV2Data } from '@/apis/activity';
import { useActivityQueueStore } from '@/store/activity-queue';
import { useActivitySessionStore } from '@/store/activity-session';

import { DEFAULT_MAX_PER_ENTRY } from '../defaults';
import { transformOfferResolve } from '../transform-offer';

import type { IQueueTask } from '../types';

/** ResolveOffersV2 冷启请求 scene（与展示 Tab 无关） */
export const COLD_START_SCENE = 'cold_start';

/** 冷启聚合窗口封顶超时（ms，§5.5 / §12） */
export const COLD_START_COLLECT_TIMEOUT_MS = 2000;

type TAggregatePhase = 'idle' | 'collecting' | 'done';

let aggregatePhase: TAggregatePhase = 'idle';
/** 本 entry 已展示过的最高优先（越大越高）；抢占用 */
let shownPriorityThisEntry: number | null = null;
/** 本 entry 是否已做过一次抢占 */
let preemptUsedThisEntry = false;

function timeout(ms: number): Promise<'timeout'> {
    return new Promise(resolve => {
        setTimeout(() => resolve('timeout'), ms);
    });
}

/** 重置聚合状态机（硬导航 / 测试调试） */
export function resetColdStartAggregate(): void {
    aggregatePhase = 'idle';
    shownPriorityThisEntry = null;
    preemptUsedThisEntry = false;
}

/**
 * 晚到：补 tryShow；若 priority 严格高于本 entry 已展示者且当前在可展示 Tab，允许一次抢占（§5.5）
 * 抢占不额外消耗 entry 计数：dismiss 后用 force 展示替换项。
 * @param task 晚到入队的任务
 */
function maybeLateArrivalShow(task: IQueueTask): void {
    const queue = useActivityQueueStore.getState();
    const { activeScene } = queue;
    // 非可展示页（reader 等 activeScene=null）只保留队列，等 SceneSync 再触发
    if (!activeScene) {
        return;
    }

    const { current } = queue;
    if (current && !preemptUsedThisEntry && shownPriorityThisEntry != null && task.priority > shownPriorityThisEntry) {
        preemptUsedThisEntry = true;
        queue.dismiss('replace', current.id);
        useActivityQueueStore.getState().tryShow(activeScene, { force: true });
        const after = useActivityQueueStore.getState().current;
        if (after) {
            shownPriorityThisEntry = after.priority;
        }
        return;
    }

    const shown = queue.tryShow(activeScene);
    if (shown) {
        const cur = useActivityQueueStore.getState().current;
        if (cur) {
            shownPriorityThisEntry =
                shownPriorityThisEntry == null ? cur.priority : Math.max(shownPriorityThisEntry, cur.priority);
        }
    }
}

/**
 * transform → enqueue / upsertSession；聚合结束后补晚到触发。
 * @param raw ResolveOffersV2 data
 * @param scene 写入 task 的场景元数据（冷启固定 cold_start）
 * @param opts.deferShow 聚合窗口内只入队不 tryShow
 * @returns 本次入队的 task 列表
 */
function applyTransformAndEnqueue(
    raw: IResolveOffersV2Data | null | undefined,
    scene: string,
    opts?: { deferShow?: boolean }
): IQueueTask[] {
    const { items } = transformOfferResolve(raw, { scene });
    const tasks: IQueueTask[] = [];

    items.forEach(item => {
        if (item.task) {
            useActivityQueueStore.getState().enqueue(item.task);
            tasks.push(item.task);
        }
        if (item.session) {
            useActivitySessionStore.getState().upsertSession(item.session);
        }
    });

    if (!opts?.deferShow) {
        // 晚到：按 priority 降序尝试补触发 / 抢占（大者先）
        const sorted = [...tasks].sort((a, b) => b.priority - a.priority);
        sorted.forEach(task => maybeLateArrivalShow(task));
    }

    return tasks;
}

function finishAggregate(): void {
    if (aggregatePhase !== 'collecting') {
        return;
    }
    aggregatePhase = 'done';

    const queue = useActivityQueueStore.getState();
    const { activeScene } = queue;
    // 当前不在可展示 Tab：只入队，展示交给后续 ActivitySceneSync.onScene
    if (activeScene) {
        queue.setMaxPerEntry(activeScene, DEFAULT_MAX_PER_ENTRY);
        queue.resetEntry(activeScene);
        queue.onScene(activeScene);
    }

    const { current } = useActivityQueueStore.getState();
    if (current) {
        shownPriorityThisEntry = current.priority;
    }
}

/**
 * 触发会话冷启拉取与聚合。
 * 会话内只跑一次；collecting 时不重复开窗（§5.8）。
 */
export async function runColdStart(): Promise<void> {
    if (aggregatePhase === 'collecting' || aggregatePhase === 'done') {
        return;
    }

    aggregatePhase = 'collecting';
    shownPriorityThisEntry = null;
    preemptUsedThisEntry = false;

    const offerPromise = ActivityApi.resolveOffersV2({
        scene: COLD_START_SCENE,
        event: '',
        action: 'query',
    })
        .then(res => {
            applyTransformAndEnqueue(res?.data, COLD_START_SCENE, {
                deferShow: aggregatePhase === 'collecting',
            });
            return 'settled' as const;
        })
        .catch(error => {
            console.error('[runColdStart] resolveOffersV2 failed', error);
            return 'settled' as const;
        });

    const raced = await Promise.race([offerPromise, timeout(COLD_START_COLLECT_TIMEOUT_MS)]);

    if (raced === 'timeout') {
        finishAggregate();
        // 超时后 offer 仍可能晚到：then 内 deferShow=false 会走晚到补触发
        offerPromise.catch(() => {});
        return;
    }

    finishAggregate();
}

/** 测试 / 调试用：当前聚合阶段 */
export function getColdStartAggregatePhase(): TAggregatePhase {
    return aggregatePhase;
}
